import { useState, useEffect, useCallback } from 'react'
import { ReviewCard } from './review-card'
import { ReviewFilters, type ReviewFiltersState, defaultFilters } from './review-filters'
import type { DateFilterValue } from './date-range-filter'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { fetchLocationReviews, type Review, type ReviewFetchFilters } from '@/lib/api/reviews'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatNumber, toIsoDateString } from '@/lib/format'

interface ReviewFeedProps {
  locationId: number
  selectedReviewId?: number
  generatingReviewId?: number
  refreshTrigger?: number
  onReviewSelect: (review: Review) => void
  onGenerateResponse: (review: Review) => void
}

// Convert UI filter state to API filter parameters
function toApiFilters(filters: ReviewFiltersState): ReviewFetchFilters | undefined {
  const apiFilters: ReviewFetchFilters = {}

  if (filters.rating !== 'all') {
    apiFilters.rating = parseInt(filters.rating)
  }

  if (filters.sentiment !== 'all') {
    apiFilters.sentiment = filters.sentiment as 'positive' | 'neutral' | 'negative'
  }

  if (filters.date.preset === 'custom') {
    // The server takes plain dates, so send the calendar day the user clicked rather
    // than an ISO instant — toISOString() would shift it across the date line for
    // anyone west of UTC.
    if (filters.date.range.from) apiFilters.dateFrom = toIsoDateString(filters.date.range.from)
    if (filters.date.range.to) apiFilters.dateTo = toIsoDateString(filters.date.range.to)
  } else if (filters.date.preset !== 'all') {
    apiFilters.datePreset = filters.date.preset as 'today' | 'week' | 'month' | 'quarter'
  }

  if (filters.responseStatus !== 'all') {
    apiFilters.responseStatus = filters.responseStatus as 'pending' | 'responded'
  }

  // Return undefined if no filters are set
  return Object.keys(apiFilters).length > 0 ? apiFilters : undefined
}

const REVIEWS_PER_PAGE = 20

export function ReviewFeed({
  locationId,
  selectedReviewId,
  generatingReviewId,
  refreshTrigger,
  onReviewSelect,
  onGenerateResponse,
}: ReviewFeedProps) {
  const { t } = useTranslation()
  const [reviews, setReviews] = useState<Review[]>([])
  const [total, setTotal] = useState(0)
  const [ratingCounts, setRatingCounts] = useState<Record<string, number>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<ReviewFiltersState>(defaultFilters)
  const [currentPage, setCurrentPage] = useState(1)

  const totalPages = Math.ceil(total / REVIEWS_PER_PAGE)

  const loadReviews = useCallback(
    async (page: number, currentFilters: ReviewFiltersState) => {
      try {
        setIsLoading(true)
        setError(null)
        const offset = (page - 1) * REVIEWS_PER_PAGE
        const response = await fetchLocationReviews(locationId, {
          limit: REVIEWS_PER_PAGE,
          offset,
          filters: toApiFilters(currentFilters),
        })
        setReviews(response.reviews)
        setTotal(response.total)
        if (response.rating_counts) {
          setRatingCounts(response.rating_counts)
        } else {
          setRatingCounts({})
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('reviews.errorLoadReviews'))
      } finally {
        setIsLoading(false)
      }
    },
    [locationId]
  )

  // Load reviews when location changes
  useEffect(() => {
    setCurrentPage(1)
    loadReviews(1, filters)
  }, [locationId])

  // Refresh when refreshTrigger changes (e.g., after saving a draft)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      loadReviews(currentPage, filters)
    }
  }, [refreshTrigger])

  // Handle filter changes
  const applyFilters = (newFilters: ReviewFiltersState) => {
    setFilters(newFilters)
    setCurrentPage(1)
    loadReviews(1, newFilters)
  }

  const handleFilterChange = (key: 'rating' | 'sentiment' | 'responseStatus', value: string) => {
    applyFilters({ ...filters, [key]: value })
  }

  const handleDateChange = (value: DateFilterValue) => {
    applyFilters({ ...filters, date: value })
  }

  const handleClearFilters = () => {
    applyFilters(defaultFilters)
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    loadReviews(page, filters)
  }

  // Calculate display range
  const startItem = total === 0 ? 0 : (currentPage - 1) * REVIEWS_PER_PAGE + 1
  const endItem = Math.min(currentPage * REVIEWS_PER_PAGE, total)

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-destructive font-medium">{error}</p>
        <Button
          variant="outline"
          onClick={() => loadReviews(currentPage, filters)}
          className="mt-4"
        >
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <ReviewFilters
        filters={filters}
        ratingCounts={ratingCounts}
        onFilterChange={handleFilterChange}
        onDateChange={handleDateChange}
        onClearFilters={handleClearFilters}
      />

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {total > 0
            ? t('reviews.showingRange', {
                start: formatNumber(startItem),
                end: formatNumber(endItem),
                total: formatNumber(total),
              })
            : t('reviews.noReviewsFound')}
        </span>
      </div>

      <div className="space-y-2">
        {isLoading ? (
          // Loading skeleton
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-4 border rounded-lg space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))
        ) : (
          <>
            {reviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                isSelected={selectedReviewId === review.id}
                isGenerating={generatingReviewId === review.id}
                onClick={() => onReviewSelect(review)}
                onGenerateResponse={() => onGenerateResponse(review)}
              />
            ))}

            {reviews.length === 0 && (
              <div className="text-center py-8">
                <p className="text-muted-foreground">{t('reviews.noReviewsMatch')}</p>
                <button
                  onClick={handleClearFilters}
                  className="text-primary hover:underline text-sm mt-2"
                >
                  {t('reviews.clearFiltersToSeeAll')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && !isLoading && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          className="pt-4"
        />
      )}
    </div>
  )
}
